import { useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Rating,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { StarRounded, StarBorderRounded, InsertChartOutlined } from "@mui/icons-material";
import moment, { Moment } from "moment";
import { useNavigate } from "react-router-dom";
import { useRelatorioAvaliacoes } from "../../hooks/useRelatorioAvaliacoes";
import { DistribuicaoNotas } from "./components/DistribuicaoNotas";
import { IndicadorSimples, NotaMediaDestaque } from "./components/IndicadorAvaliacoes";
import {
    Cartao,
    CabecalhoCartao,
    CelulaCabecalho,
    CelulaCorpo,
    EstadoVazio,
    LinhaTabela,
    TituloCartao,
} from "../../../core/styles/superficies";

const formatoApi = (data: Moment) => data.format("YYYY-MM-DD");
const dataHoraBr = (valor: string) => moment(valor).format("DD/MM/YYYY HH:mm");

export const RelatorioAvaliacoes = () => {
    const navigate = useNavigate();

    // Últimos 30 dias: período curto o bastante para refletir a operação atual
    // e longo o bastante para um restaurante ter volume de resposta.
    const [dataInicio, setDataInicio] = useState<Moment>(moment().subtract(30, "days"));
    const [dataFim, setDataFim] = useState<Moment>(moment());
    const [filtro, setFiltro] = useState({
        inicio: formatoApi(moment().subtract(30, "days")),
        fim: formatoApi(moment()),
    });

    const { relatorio, isLoading } = useRelatorioAvaliacoes(filtro.inicio, filtro.fim);

    const periodoInvalido = dataInicio.isAfter(dataFim, "day");

    const aplicar = () => {
        if (periodoInvalido) return;
        setFiltro({ inicio: formatoApi(dataInicio), fim: formatoApi(dataFim) });
    };

    const total = relatorio?.totalAvaliacoes ?? 0;
    const comComentario = relatorio?.totalComComentario ?? 0;
    const percentualComentario = total > 0 ? Math.round((comComentario / total) * 100) : 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: { xs: 1.5, md: 2 } }}>
            <Typography variant="h6" fontWeight={700}>
                Avaliações dos clientes
            </Typography>

            {/* Filtros em uma linha, acima do conteúdo que eles controlam. */}
            <Cartao sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
                    <DatePicker
                        label="Data início"
                        format="DD/MM/YYYY"
                        value={dataInicio}
                        onChange={(data) => data && setDataInicio(moment(data))}
                        slotProps={{ textField: { size: "small", error: periodoInvalido } }}
                    />
                    <DatePicker
                        label="Data fim"
                        format="DD/MM/YYYY"
                        value={dataFim}
                        onChange={(data) => data && setDataFim(moment(data))}
                        slotProps={{
                            textField: {
                                size: "small",
                                error: periodoInvalido,
                                helperText: periodoInvalido ? "A data inicial não pode ser maior que a final" : undefined,
                            },
                        }}
                    />
                    <Button variant="contained" onClick={aplicar} disabled={periodoInvalido || isLoading} sx={{ height: 40 }}>
                        Aplicar
                    </Button>
                </Box>
            </Cartao>

            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <NotaMediaDestaque nota={relatorio?.notaMedia ?? null} total={total} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <IndicadorSimples
                                rotulo="Com comentário"
                                valor={comComentario}
                                apoio={total > 0 ? `${percentualComentario}% das avaliações do período` : "Sem avaliações no período"}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Cartao sx={{ height: "100%", p: 2.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: .4 }}>
                                    Distribuição
                                </Typography>
                                <Box sx={{ mt: 1.5 }}>
                                    <DistribuicaoNotas distribuicao={relatorio?.distribuicao ?? {}} total={total} />
                                </Box>
                            </Cartao>
                        </Grid>
                    </Grid>

                    <Cartao>
                        <CabecalhoCartao>
                            <TituloCartao>Respostas</TituloCartao>
                        </CabecalhoCartao>

                        {relatorio?.itens?.length ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <CelulaCabecalho>Nº pedido</CelulaCabecalho>
                                            <CelulaCabecalho>Cliente</CelulaCabecalho>
                                            <CelulaCabecalho>Nota</CelulaCabecalho>
                                            <CelulaCabecalho>Comentário</CelulaCabecalho>
                                            <CelulaCabecalho>Respondida em</CelulaCabecalho>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {relatorio.itens.map((item) => (
                                            <LinhaTabela
                                                key={item.pedidoUuid}
                                                hover
                                                sx={{ cursor: "pointer" }}
                                                onClick={() => navigate(`/pedidos/${item.pedidoUuid}`)}
                                            >
                                                <CelulaCorpo>{item.numeroPedido}</CelulaCorpo>
                                                <CelulaCorpo>{item.nomeCliente ?? "-"}</CelulaCorpo>
                                                <CelulaCorpo>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: .75 }}>
                                                        <Rating
                                                            value={item.nota}
                                                            readOnly
                                                            size="small"
                                                            icon={<StarRounded fontSize="inherit" />}
                                                            emptyIcon={<StarBorderRounded fontSize="inherit" />}
                                                        />
                                                        <Typography variant="body2" fontWeight={600}>{item.nota}</Typography>
                                                    </Box>
                                                </CelulaCorpo>
                                                <CelulaCorpo sx={{ maxWidth: 380, whiteSpace: "normal" }}>
                                                    {item.comentario
                                                        ? <Typography variant="body2" sx={{ fontStyle: "italic" }}>“{item.comentario}”</Typography>
                                                        : <Typography variant="body2" color="text.disabled">—</Typography>}
                                                </CelulaCorpo>
                                                <CelulaCorpo>{dataHoraBr(item.dataHoraResposta)}</CelulaCorpo>
                                            </LinhaTabela>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <EstadoVazio>
                                <InsertChartOutlined sx={{ fontSize: 28, opacity: .5 }} />
                                <Typography variant="body2">Nenhuma avaliação respondida neste período</Typography>
                            </EstadoVazio>
                        )}
                    </Cartao>
                </>
            )}
        </Box>
    );
};
